import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, LineReveal } from "../lib/motion";

const paragraphs = [
  "I'm passionate about helping people promote and position themselves with confidence so they can fulfill the call God has placed inside them. Social media can be more than entertainment — it can be an outlet for purpose, service, connection, and impact.",
  "My experience in government environments, including the Statehouse, gave me opportunities to interact with state representatives, senators, and their staff. I noticed a recurring gap: many public servants do meaningful work, but their online presence doesn't clearly communicate who they are, what they represent, or how they serve.",
  "Civic leaders carry full schedules — meetings, complex issues, community events — and rarely have time to capture it. Kingdom Advancement helps them share that work in a way that feels clear, credible, human, and engaging.",
  "My vision also includes weddings and signature events. After working in wedding setups, I saw how many meaningful moments happen behind the scenes — and how invisible wedding content creation is in the Columbus market. Couples deserve a budget-friendly way to preserve the candid moments of their day.",
  "Ohio is home to incredible small restaurants and local businesses that deserve greater visibility. Businesses shouldn't have to rely only on someone else's platform. Consistent content on their own pages builds recognition, showcases personality, and turns attention into loyal customers.",
];

export const Founder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="founder" className="relative bg-cream text-navy py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        <Reveal>
          <p className="text-gold-dark text-xs uppercase tracking-luxe mb-4">Behind The Brand</p>
          <h2 className="font-display uppercase text-navy text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-4xl">
            <LineReveal lines={["Behind every brand", "is a purpose."]} />
          </h2>
          <p className="font-display italic text-navy/60 text-2xl md:text-3xl mt-4">
            Behind Kingdom Advancement is a calling.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 mt-16 items-start">
          {/* portrait */}
          <div ref={ref} className="relative lg:sticky lg:top-28">
            <div className="relative overflow-hidden rounded-t-[180px] rounded-b-2xl border border-gold/30 shadow-[0_30px_80px_rgba(10,27,53,0.25)]">
              <motion.img
                src="/founder.jpg"
                alt="O'Shea Johnson, Founder and CEO"
                style={{ y }}
                className="w-full aspect-[3/4] object-cover scale-110"
              />
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gold" />
            </div>
            <div className="mt-6 text-center">
              <p className="font-display text-3xl text-navy">O'Shea Johnson</p>
              <p className="text-gold-dark text-xs uppercase tracking-luxe mt-1">Founder & CEO</p>
            </div>
            <div className="absolute -z-10 -top-6 -left-6 w-40 h-40 rounded-full bg-gold/15 blur-2xl" />
          </div>

          {/* letter */}
          <div>
            <Reveal>
              <p className="font-display text-2xl md:text-3xl text-navy leading-snug mb-8">
                I'm O'Shea Johnson, Founder and CEO of Kingdom Advancement Social
                Media Marketing, LLC.
              </p>
            </Reveal>
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className="text-navy/75 font-light leading-relaxed mb-6 md:text-lg">{p}</p>
              </Reveal>
            ))}
            <Reveal>
              <div className="border-l-2 border-gold pl-6 mt-4">
                <p className="text-navy font-display italic text-2xl md:text-3xl leading-snug">
                  To civic leaders, couples, restaurant owners, creators, and local
                  businesses ready for elevation — I'm excited to serve you.
                </p>
                <p className="text-gold-dark uppercase tracking-luxe text-sm mt-6">
                  Let's advance your story.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
